import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { Loading, ErrorMessage } from '@/src/ui';
import type { ClassDTO } from '../models/classDTOs';

interface DeleteClassModalProps {
    open: boolean;
    classItem?: ClassDTO | null;
    onClose: () => void;
    setError?: (msg: string) => void;
    onSubmitCallback?: () => void;
    error?: string;
}

import { apiRequest } from '@/src/services';

const DeleteClassModal: React.FC<DeleteClassModalProps> = ({
    open,
    classItem,
    onClose,
    setError,
    onSubmitCallback,
    error,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [firstConfirm, setFirstConfirm] = useState(false);
    const [localError, setLocalError] = useState('');

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();
        if (submitting || success) return;
        if (!classItem?.classId) {
            setLocalError('Unable to load class Id');
            return;
        }
        if (!firstConfirm) {
            setFirstConfirm(true);
            return;
        }
        setSubmitting(true);
        setLocalError('');
        try {
            await apiRequest('DELETE', `dbo/classes/${classItem.classId}`);
            setSuccess(true);
            setTimeout(() => {
                if (onSubmitCallback) onSubmitCallback();
                setFirstConfirm(false);
                setSuccess(false);
            }, 750);
        } catch {
            setLocalError('Failed to delete class');
            if (setError) setError('Failed to delete class');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>Delete Class</h2>
                <p>Are you sure you want to delete this class?</p>
                <p>
                    <strong>Name:</strong> {classItem?.className}
                </p>
                <p>
                    <strong>School:</strong> {classItem?.schoolName}
                </p>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {!firstConfirm ? (
                        <>
                            <Button variant="outlined" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Delete
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                disabled={submitting || success}
                                variant="contained"
                                color="error"
                                onClick={handleSubmit}
                            >
                                Confirm Delete
                            </Button>
                            <Button
                                disabled={submitting || success}
                                variant="outlined"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                <ErrorMessage error={localError || error} />
                {success && (
                    <div style={{ color: 'green', marginTop: 8 }}>Class deleted successfully!</div>
                )}
                {submitting && <Loading message="Processing..." size="sm" />}
            </Box>
        </Modal>
    );
};

export default DeleteClassModal;
